import * as React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';

interface ConsumptionProgressBarProps {
  percentageValue: number;
  usageCategory: string;
  typicalRange: {
    low: number;
    high: number;
  };
  width?: string | number;
  typicalLowLimit?: number;
  typicalHighLimit?: number;
}

const ConsumptionProgressBar: React.FC<ConsumptionProgressBarProps> = ({
  percentageValue,
  usageCategory,
  typicalRange,
  width = '100%',
  typicalLowLimit = 30,
  typicalHighLimit = 40
}) => {
  const theme = useTheme();
  
  // Helper function to determine usage category based on configurable limits
  const getUsageCategory = (value: number): string => {
    if (value < typicalLowLimit) return 'below_average';
    if (value <= typicalHighLimit) return 'typical';
    return 'above_average';
  };

  const calculatedUsageCategory = getUsageCategory(percentageValue);
  
  // Theme-aware colors for dark mode compatibility
  const getProgressBarColors = () => {
    if (theme.palette.mode === 'dark') {
      return {
        background: theme.palette.grey[700],
        low: theme.palette.success.main,
        typical: theme.palette.grey[600],
        high: theme.palette.error.main,
        indicator: theme.palette.common.white
      };
    } else {
      return {
        background: '#e0e0e0',
        low: '#4caf50',
        typical: '#9e9e9e',
        high: '#f44336',
        indicator: '#000'
      };
    }
  };

  const colors = getProgressBarColors();
  
  return (
    <Box sx={{ width, mb: 1 }}>
      {/* Scale labels above the bar */}
      <Box sx={{ 
        position: 'relative',
        mb: 0.5,
        height: 20
      }}>
        <Typography variant="caption" color="text.secondary" sx={{ position: 'absolute', left: 0, top: 0, fontWeight: 'bold' }}>
          0%
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ position: 'absolute', left: `${typicalLowLimit}%`, transform: 'translateX(-50%)', top: 0, fontWeight: 'bold' }}>
          {typicalLowLimit}%
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ position: 'absolute', left: `${typicalHighLimit}%`, transform: 'translateX(-50%)', top: 0, fontWeight: 'bold' }}>
          {typicalHighLimit}%
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ position: 'absolute', right: 0, top: 0, fontWeight: 'bold' }}>
          100%
        </Typography>
      </Box>
      
      {/* Progress bar */}
      <Box sx={{
        width: '100%',
        height: 8,
        backgroundColor: colors.background,
        borderRadius: 4,
        position: 'relative'
      }}>
        {/* Green zone (below average) */}
        <Box sx={{
          position: 'absolute',
          left: 0,
          top: 0,
          width: `${typicalLowLimit}%`,
          height: '100%',
          backgroundColor: colors.low,
          borderRadius: '4px 0 0 4px'
        }} />
        {/* Grey zone (typical) */}
        <Box sx={{
          position: 'absolute',
          left: `${typicalLowLimit}%`,
          top: 0,
          width: `${typicalHighLimit - typicalLowLimit}%`,
          height: '100%',
          backgroundColor: colors.typical
        }} />
        {/* Red zone (above average) */}
        <Box sx={{
          position: 'absolute',
          right: 0,
          top: 0,
          width: `${100 - typicalHighLimit}%`,
          height: '100%',
          backgroundColor: colors.high,
          borderRadius: '0 4px 4px 0'
        }} />
        
        {/* Enhanced user consumption region with double height and animation */}
        <Box sx={{
          position: 'absolute',
          left: 0,
          top: -4,
          width: `${Math.min((percentageValue / 100) * 100, 95)}%`,
          height: 16,
          backgroundColor: calculatedUsageCategory === 'below_average' ? `${colors.low}4D` :
                          calculatedUsageCategory === 'typical' ? `${colors.typical}4D` :
                          `${colors.high}4D`,
          borderRadius: '4px 0 0 4px',
          border: `2px solid ${calculatedUsageCategory === 'below_average' ? colors.low :
                          calculatedUsageCategory === 'typical' ? colors.typical :
                          colors.high}`,
          overflow: 'hidden'
        }}>
          {/* Animated energy flow effect */}
          <Box sx={{
            position: 'absolute',
            left: 0,
            top: 0,
            width: '100%',
            height: '100%',
            background: `linear-gradient(90deg, transparent 0%, ${calculatedUsageCategory === 'below_average' ? `${colors.low}CC` :
                          calculatedUsageCategory === 'typical' ? `${colors.typical}CC` :
                          `${colors.high}CC`} 50%, transparent 100%)`,
            animation: 'energyFlow 2s ease-in-out infinite',
            '@keyframes energyFlow': {
              '0%': {
                transform: 'translateX(-100%)'
              },
              '100%': {
                transform: 'translateX(100%)'
              }
            }
          }} />
        </Box>
        
        {/* User's position indicator */}
        <Box sx={{
          position: 'absolute',
          left: `${Math.min((percentageValue / 100) * 100, 95)}%`,
          top: -6,
          width: 4,
          height: 20,
          backgroundColor: colors.indicator,
          borderRadius: 2,
          transform: 'translateX(-50%)'
        }} />
      </Box>
      
      {/* Region labels below the bar */}
      <Box sx={{ 
        position: 'relative',
        mt: 0.5 
      }}>
        <Typography variant="caption" color="text.secondary" sx={{ position: 'absolute', left: 0, fontWeight: 'bold' }}>
          Low
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ position: 'absolute', left: `${(typicalLowLimit + typicalHighLimit) / 2}%`, transform: 'translateX(-50%)', fontWeight: 'bold' }}>
          Typical
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ position: 'absolute', right: 0, fontWeight: 'bold' }}>
          High
        </Typography>
      </Box>
    </Box>
  );
};

export default ConsumptionProgressBar;

/*
Usage Examples:

// Default usage (30-40% typical range)
<ConsumptionProgressBar
  percentageValue={35}
  usageCategory="typical"
  typicalRange={{ low: 30, high: 40 }}
/>

// Custom typical range (25-35%)
<ConsumptionProgressBar
  percentageValue={30}
  usageCategory="typical"
  typicalRange={{ low: 25, high: 35 }}
  typicalLowLimit={25}
  typicalHighLimit={35}
/>

// Different width
<ConsumptionProgressBar
  percentageValue={45}
  usageCategory="above_average"
  typicalRange={{ low: 30, high: 40 }}
  width="60%"
/>
*/
